import React from 'react';

export const TransactionConfirmationModal = ({
  showTransactionConfirm,
  setShowTransactionConfirm,
  pendingTransaction,
  confirmTransaction,
  cancelTransaction,
  loading,
  error,
  setError,
  formatUSD,
  ethPrice
}) => {
  // Debug info (safe to keep while testing; remove in prod)
  console.debug('🔍 TransactionConfirmationModal Debug:', {
    showTransactionConfirm,
    hasPendingTransaction: !!pendingTransaction,
    pendingTransactionPreview: pendingTransaction ? (
      pendingTransaction.transaction
        ? { ...pendingTransaction.transaction, envelope: true }
        : { ...pendingTransaction, envelope: false }
    ) : null
  });

    // 🔍 DIAGNOSTIC LOGS - ADD THESE LINES
  console.log('🔍 TransactionConfirmationModal - received props:', {
    showTransactionConfirm,
    hasPendingTransaction: !!pendingTransaction,
    pendingTransactionType: pendingTransaction?.type,
    pendingTransactionKeys: pendingTransaction ? Object.keys(pendingTransaction) : null,
    pendingTransaction: pendingTransaction
  });

  // If the modal is not requested to be shown, do nothing.
  if (!showTransactionConfirm) return null;

  // If the pendingTransaction details are not yet available, show a small waiting placeholder.
  if (!pendingTransaction) {
    return (
      <div style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
        <div style={{
          background: 'var(--card-bg)',
          padding: '24px',
          borderRadius: '12px',
          border: '2px solid var(--gold)',
          width: '100%',
          maxWidth: '420px',
          boxShadow: '0 0 30px var(--gold)',
          textAlign: 'center'
        }}>
          <h3 style={{ color: 'var(--gold)', margin: '0 0 12px 0', fontSize: '20px' }}>
            Preparing transaction…
          </h3>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            {/* Cancel (existing behavior) */}
            <button
              style={{ flex: '1 1 0%', background: 'transparent', color: 'rgb(255, 68, 68)', border: '1px solid rgb(255, 68, 68)', padding: '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
              onClick={() => {
                try { cancelTransaction && cancelTransaction(); } catch (e) { console.warn('Cancel handler failed', e); }
              }}
            >
              Cancel
            </button>

            {/* Confirm/Waiting button: enable as soon as we have txRequest OR preflight OR display */}
            {(() => {
              const readyToConfirm = !!(pendingTransaction && (pendingTransaction.txRequest || pendingTransaction.preflight || pendingTransaction.display));
              return (
                <button
                  disabled={!readyToConfirm}
                  onClick={() => {
                    try { if (readyToConfirm) { confirmTransaction && confirmTransaction(); } }
                    catch (e) { console.error('Confirm handler failed', e); }
                  }}
                  style={{
                    flex: '1 1 0%',
                    background: readyToConfirm ? 'var(--gold)' : 'rgb(119,119,119)',
                    color: readyToConfirm ? 'var(--black)' : 'var(--text-secondary)',
                    border: 'none',
                    padding: '10px',
                    borderRadius: '8px',
                    cursor: readyToConfirm ? 'pointer' : 'not-allowed',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                >
                  {readyToConfirm ? 'Confirm' : 'Waiting…'}
                </button>
              );
            })()}
          </div>

          <div style={{ marginTop: 12, color: 'var(--text-secondary)', fontSize: 12 }}>
            If this takes longer than expected, check the wallet window or unlock your wallet.
          </div>
        </div>
      </div>
    );
  }

  // Normalize pendingTransaction shape (envelope vs flat)
  const tx = pendingTransaction && (pendingTransaction.transaction ? pendingTransaction.transaction : pendingTransaction);

  // Debug normalized tx (temporary)
  console.debug('🔍 TransactionConfirmationModal normalized tx:', tx, 'envelope:', !!pendingTransaction.transaction);

  // If tx indicates an ENS operation (tx.type present), render ENS UI
  if (tx && tx.type) {
    const type = tx.type;
    const ensName = tx.ensName || tx.name || tx.domain || tx.node || '';
    const textKey = tx.key || tx.recordKey || tx.textKey || '';
    const textValue = (typeof tx.value !== 'undefined') ? tx.value : (tx.textValue || tx.recordValue || '');
    const avatarUrl = tx.avatarUrl || tx.avatar || '';

    // Disable confirm if essential fields missing (defensive)
    const canConfirmENS = !!type && (!!ensName || !!textKey || !!textValue || !!avatarUrl);

    return (
      <div style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
        <div style={{
          background: 'var(--card-bg)',
          padding: '30px',
          borderRadius: '15px',
          border: '2px solid var(--gold)',
          width: '100%',
          maxWidth: '560px',
          boxShadow: '0 0 50px var(--gold)'
        }}>
          <h3 style={{ color: 'var(--gold)', margin: '0 0 20px 0', fontSize: '24px', textAlign: 'center' }}>
            Confirm ENS Transaction
          </h3>

          <div style={{
            background: 'var(--black)',
            padding: '20px',
            borderRadius: '10px',
            border: '1px solid var(--gold)',
            marginBottom: '20px'
          }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: 6 }}>Action</div>
              <div style={{ color: 'var(--text-light)', fontSize: 14 }}>{type}</div>
            </div>

            {ensName && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: 6 }}>ENS Name</div>
                <div style={{ color: 'var(--text-light)', fontFamily: 'monospace', fontSize: 14, wordBreak: 'break-all' }}>{ensName}</div>
              </div>
            )}

            {textKey && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: 6 }}>Record Key</div>
                <div style={{ color: 'var(--text-light)', fontSize: 14 }}>{textKey}</div>
              </div>
            )}

            {textValue && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: 6 }}>Record Value</div>
                <div style={{ color: 'var(--text-light)', fontFamily: 'monospace', fontSize: 14, wordBreak: 'break-all' }}>{textValue}</div>
              </div>
            )}

            {avatarUrl && (
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
                <div style={{ color: 'var(--gold)', fontWeight: 'bold' }}>Avatar</div>
                <img src={avatarUrl} alt="avatar preview" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.06)' }} />
                <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{avatarUrl}</div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={() => { try { confirmTransaction && confirmTransaction(); } catch (e) { console.warn(e); } }}
              disabled={loading || !canConfirmENS}
              style={{
                flex: 1,
                background: (loading || !canConfirmENS) ? '#666' : 'var(--gold)',
                color: 'var(--black)',
                border: 'none',
                padding: '15px',
                borderRadius: '8px',
                cursor: (loading || !canConfirmENS) ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                fontSize: '16px'
              }}
            >
              {loading ? 'Processing...' : 'Confirm & Send'}
            </button>

            <button
              onClick={() => { try { cancelTransaction && cancelTransaction(); } catch (e) { console.warn(e); } try { setShowTransactionConfirm(false); } catch (e) {} }}
              disabled={loading}
              style={{
                flex: 1,
                background: 'transparent',
                color: '#ff4444',
                border: '1px solid #ff4444',
                padding: '15px',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                fontSize: '16px'
              }}
            >
              Cancel
            </button>
          </div>

          {error && (
            <div style={{
              background: 'rgba(255, 68, 68, 0.1)',
              border: '1px solid #ff4444',
              color: '#ff4444',
              padding: '12px',
              borderRadius: '5px',
              marginTop: '15px',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}

        </div>
      </div>
    );
  }

  // Default: regular ETH transfer UI (uses normalized tx)
  const { to, value, from } = tx || {};
  const numericValue = (value !== undefined && value !== null)
    ? value
    : (tx && (tx.amount || tx.value) ? (tx.amount || tx.value) : '0');
  const usdValue = Number(numericValue) * (Number(ethPrice) || 0);

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.9)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'var(--card-bg)',
        padding: '30px',
        borderRadius: '15px',
        border: '2px solid var(--gold)',
        width: '100%',
        maxWidth: '500px',
        boxShadow: '0 0 50px var(--gold)'
      }}>
        <h3 style={{ color: 'var(--gold)', margin: '0 0 20px 0', fontSize: '24px', textAlign: 'center' }}>
          Confirm Transaction
        </h3>

        <div style={{
          background: 'var(--black)',
          padding: '20px',
          borderRadius: '10px',
          border: '1px solid var(--gold)',
          marginBottom: '25px'
        }}>
          <div style={{ marginBottom: '15px' }}>
            <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>From:</div>
            <div style={{ color: 'var(--text-light)', fontFamily: 'monospace', fontSize: '14px', wordBreak: 'break-all' }}>{from}</div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>To:</div>
            <div style={{ color: 'var(--text-light)', fontFamily: 'monospace', fontSize: '14px', wordBreak: 'break-all' }}>{to}</div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>Amount:</div>
            <div style={{ color: 'var(--text-light)', fontSize: '18px', fontWeight: 'bold' }}>
              {numericValue} ETH
              {ethPrice > 0 && (
                <div style={{ color: 'var(--text-secondary)', fontSize: '14px', marginTop: '5px' }}>
                  ≈ {formatUSD ? formatUSD(usdValue) : `${usdValue.toFixed(2)} USD`}
                </div>
              )}
            </div>
          </div>

          <div>
            <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>Network:</div>
            <div style={{ color: 'var(--text-light)', fontSize: '14px' }}>Ethereum Mainnet</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => { try { confirmTransaction && confirmTransaction(); } catch (e) { console.warn(e); } }}
            disabled={loading}
            style={{
              flex: 1,
              background: loading ? '#666' : 'var(--gold)',
              color: 'var(--black)',
              border: 'none',
              padding: '15px',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              fontSize: '16px'
            }}
          >
            {loading ? 'Processing...' : 'Confirm & Send'}
          </button>

          <button
            onClick={() => { try { cancelTransaction && cancelTransaction(); } catch (e) { console.warn(e); } try { setShowTransactionConfirm(false); } catch (e) {} }}
            disabled={loading}
            style={{
              flex: 1,
              background: 'transparent',
              color: '#ff4444',
              border: '1px solid #ff4444',
              padding: '15px',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              fontSize: '16px'
            }}
          >
            Cancel
          </button>
        </div>

        {error && (
          <div style={{
            background: 'rgba(255, 68, 68, 0.1)',
            border: '1px solid #ff4444',
            color: '#ff4444',
            padding: '12px',
            borderRadius: '5px',
            marginTop: '15px',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <div style={{
          background: 'var(--gold-shadow)',
          padding: '15px',
          borderRadius: '8px',
          marginTop: '20px',
          border: '1px solid var(--gold)'
        }}>
          <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '12px', lineHeight: '1.4' }}>
            <strong>Note:</strong> Please verify all transaction details before confirming. Transactions cannot be reversed once confirmed.
          </p>
        </div>
      </div>
    </div>
  );
};

// Transaction Receipt Modal
export const TransactionReceiptModal = ({
  showTransactionReceipt,
  setShowTransactionReceipt,
  transactionReceipt,
  loading,
  error
}) => {
  if (!showTransactionReceipt || !transactionReceipt) return null;

  const { hash, status, from, to, value, gasUsed, blockNumber } = transactionReceipt;

  const getStatusColor = () => {
    switch (status) {
      case 'pending': return 'var(--gold)';
      case 'confirmed': return '#00ff88';
      case 'failed': return '#ff4444';
      default: return 'var(--text-secondary)';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'confirmed': return 'Confirmed';
      case 'failed': return 'Failed';
      default: return 'Unknown';
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'var(--card-bg)',
        padding: '30px',
        borderRadius: '15px',
        border: '2px solid var(--gold)',
        width: '100%',
        maxWidth: '500px',
        boxShadow: '0 0 50px var(--gold)'
      }}>
        <h3 style={{ color: 'var(--gold)', margin: '0 0 20px 0', fontSize: '24px', textAlign: 'center' }}>
          Transaction {status === 'confirmed' ? 'Confirmed' : 'Receipt'}
        </h3>

        <div style={{
          background: 'var(--black)',
          padding: '20px',
          borderRadius: '10px',
          border: '1px solid var(--gold)',
          marginBottom: '25px'
        }}>
          <div style={{
            background: `rgba(${status === 'confirmed' ? '0, 255, 136' : status === 'failed' ? '255, 68, 68' : '255, 215, 0'}, 0.1)`,
            border: `1px solid ${getStatusColor()}`,
            color: getStatusColor(),
            padding: '10px 15px',
            borderRadius: '6px',
            marginBottom: '20px',
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '16px'
          }}>
            {status === 'pending' ? '⏳ Transaction Pending' :
             status === 'confirmed' ? '✅ Transaction Confirmed' :
             '❌ Transaction Failed'}
          </div>

          <div style={{ marginBottom: '15px' }}>
            <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>Status:</div>
            <div style={{ color: getStatusColor(), fontSize: '14px', fontWeight: 'bold' }}>
              {getStatusText()}
            </div>
          </div>

          {hash && (
            <div style={{ marginBottom: '15px' }}>
              <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>Transaction Hash:</div>
              <div style={{ color: 'var(--text-light)', fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>
                {hash}
              </div>
            </div>
          )}

          {blockNumber && (
            <div style={{ marginBottom: '15px' }}>
              <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>Block Number:</div>
              <div style={{ color: 'var(--text-light)', fontSize: '14px' }}>
                {blockNumber}
              </div>
            </div>
          )}

          <div style={{ marginBottom: '15px' }}>
            <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>From:</div>
            <div style={{ color: 'var(--text-light)', fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>
              {from}
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>To:</div>
            <div style={{ color: 'var(--text-light)', fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>
              {to}
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>Amount:</div>
            <div style={{ color: 'var(--text-light)', fontSize: '16px', fontWeight: 'bold' }}>
              {value} ETH
            </div>
          </div>

          {gasUsed && (
            <div>
              <div style={{ color: 'var(--gold)', fontWeight: 'bold', marginBottom: '5px' }}>Gas Used:</div>
              <div style={{ color: 'var(--text-light)', fontSize: '14px' }}>
                {gasUsed}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setShowTransactionReceipt(false)}
            style={{
              flex: 1,
              background: 'transparent',
              color: '#ff4444',
              border: '1px solid #ff4444',
              padding: '15px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '16px'
            }}
          >
            Close
          </button>

          {hash && status !== 'failed' && (
            <button
              onClick={() => window.open(`https://etherscan.io/tx/${hash}`, '_blank')}
              style={{
                flex: 1,
                background: 'var(--gold)',
                color: 'var(--black)',
                border: 'none',
                padding: '15px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '16px'
              }}
            >
              View on Etherscan
            </button>
          )}
        </div>

        {error && (
          <div style={{
            background: 'rgba(255, 68, 68, 0.1)',
            border: '1px solid #ff4444',
            color: '#ff4444',
            padding: '12px',
            borderRadius: '5px',
            marginTop: '15px',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

// Send Modal
export const SendModal = ({
  showSendModal,
  setShowSendModal,
  sendTo,
  setSendTo,
  sendAmount,
  setSendAmount,
  balance,
  ethPrice,
  setPendingTransaction,
  setShowTransactionConfirm,
  loading,
  error,
  setError,
  formatUSD,
  account
}) => {
  if (!showSendModal) return null;

  const handleSendClick = () => {
    if (!sendTo || !sendAmount) {
      setError('Please enter recipient address and amount');
      return;
    }

    if (parseFloat(sendAmount) > parseFloat(balance)) {
      setError('Insufficient balance');
      return;
    }

    setPendingTransaction({
      to: sendTo,
      value: sendAmount,
      from: account
    });
    setShowTransactionConfirm(true);
    setShowSendModal(false);
    setError('');
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'var(--card-bg)',
        padding: '30px',
        borderRadius: '15px',
        border: '2px solid var(--gold)',
        width: '100%',
        maxWidth: '450px',
        boxShadow: '0 0 50px var(--gold)'
      }}>
        <h3 style={{ color: 'var(--gold)', margin: '0 0 20px 0', fontSize: '24px', textAlign: 'center' }}>
          Send Ethereum
        </h3>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', color: 'var(--gold)', marginBottom: '8px', fontWeight: 'bold' }}>
            Recipient Address
          </label>
          <input
            type="text"
            placeholder="0x..."
            value={sendTo}
            onChange={(e) => setSendTo(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              background: 'var(--black)',
              color: 'var(--gold)',
              border: '1px solid var(--gold)',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'monospace'
            }}
          />
        </div>

        <div style={{ marginBottom: '25px' }}>
          <label style={{ display: 'block', color: 'var(--gold)', marginBottom: '8px', fontWeight: 'bold' }}>
            Amount (ETH)
          </label>
          <input
            type="number"
            placeholder="0.00"
            value={sendAmount}
            onChange={(e) => setSendAmount(e.target.value)}
            step="0.000001"
            min="0"
            max={balance}
            style={{
              width: '100%',
              padding: '12px',
              background: 'var(--black)',
              color: 'var(--gold)',
              border: '1px solid var(--gold)',
              borderRadius: '8px',
              fontSize: '16px'
            }}
          />
          <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '5px' }}>
            Available: {parseFloat(balance).toFixed(6)} ETH
            {ethPrice > 0 && ` (${formatUSD(parseFloat(balance) * ethPrice)})`}
          </div>
          {sendAmount && ethPrice > 0 && (
            <div style={{ color: 'var(--gold)', fontSize: '12px', marginTop: '5px', fontWeight: 'bold' }}>
              ≈ {formatUSD(parseFloat(sendAmount) * ethPrice)}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={handleSendClick}
            disabled={loading || !sendTo || !sendAmount}
            style={{
              flex: 1,
              background: loading ? '#666' : 'var(--gold)',
              color: 'var(--black)',
              border: 'none',
              padding: '15px',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              fontSize: '16px'
            }}
          >
            {loading ? 'Processing...' : 'Review Transaction'}
          </button>

          <button
            onClick={() => {
              setShowSendModal(false);
              setError('');
              setSendTo('');
              setSendAmount('');
            }}
            style={{
              flex: 1,
              background: 'transparent',
              color: '#ff4444',
              border: '1px solid #ff4444',
              padding: '15px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '16px'
            }}
          >
            Cancel
          </button>
        </div>

        {error && (
          <div style={{
            background: 'rgba(255, 68, 68, 0.1)',
            border: '1px solid #ff4444',
            color: '#ff4444',
            padding: '12px',
            borderRadius: '5px',
            marginTop: '15px',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

// Receive Modal
export const ReceiveModal = ({
  showReceiveModal,
  setShowReceiveModal,
  account,
  copyAddress
}) => {
  if (!showReceiveModal) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'var(--card-bg)',
        padding: '30px',
        borderRadius: '15px',
        border: '2px solid var(--gold)',
        width: '100%',
        maxWidth: '450px',
        boxShadow: '0 0 50px var(--gold)',
        textAlign: 'center'
      }}>
        <h3 style={{ color: 'var(--gold)', margin: '0 0 20px 0', fontSize: '24px' }}>
          Receive Assets
        </h3>

        <div style={{
          background: 'var(--black)',
          padding: '20px',
          borderRadius: '10px',
          border: '1px solid var(--gold)',
          marginBottom: '20px',
          wordBreak: 'break-all'
        }}>
          <p style={{ color: 'var(--gold)', margin: '0 0 10px 0', fontWeight: 'bold' }}>
            Your Wallet Address:
          </p>
          <p style={{ color: 'var(--text-light)', margin: 0, fontFamily: 'monospace', fontSize: '14px' }}>
            {account}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={copyAddress}
            style={{
              flex: 1,
              background: 'var(--gold)',
              color: 'var(--black)',
              border: 'none',
              padding: '15px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '16px'
            }}
          >
            Copy Address
          </button>

          <button
            onClick={() => setShowReceiveModal(false)}
            style={{
              flex: 1,
              background: 'transparent',
              color: '#ff4444',
              border: '1px solid #ff4444',
              padding: '15px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '16px'
            }}
          >
            Close
          </button>
        </div>

        <div style={{
          background: 'var(--gold-shadow)',
          padding: '15px',
          borderRadius: '8px',
          marginTop: '20px',
          border: '1px solid var(--gold)'
        }}>
          <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '12px', lineHeight: '1.4' }}>
            <strong>Pro Tip:</strong> Share your wallet address for receiving assets.
          </p>
        </div>
      </div>
    </div>
  );
};

// Main modal orchestrator - INCLUDES ALL MODALS
const ZwalletModals = (props) => {
  return (
    <>
      <SendModal {...props} />
      <ReceiveModal {...props} />
      <TransactionConfirmationModal {...props} />
      <TransactionReceiptModal {...props} />
    </>
  );
};

export default ZwalletModals;
